// utilities/ticketCreateForm.js
//
// PURE decision logic for the create-ticket modal (SOUND-544 prefill +
// default priority, SOUND-742 discard guard). The modal only renders; every
// "should this field change?" / "is there anything to lose?" answer lives here
// so it is unit-tested (sound-app has no component harness — SOUND-679).

/**
 * Next value of a template-prefilled field (title / description).
 *
 * Rule: an UNTOUCHED field mirrors the rendered template — and clears when the
 * template goes away (request type switched to one without a template), so a
 * stale prefill from the previous type never lingers. Once the user has edited
 * the field by hand it is theirs: the template never overwrites it again.
 *
 * @param {{current: string, rendered: string|null, touched: boolean}} args
 * @returns {string} the value the field should hold
 */
export function nextPrefillValue({ current, rendered, touched }) {
  if (touched) return current;
  return typeof rendered === "string" ? rendered : "";
}

/**
 * Priority to show after the request type (or its defaultPriority) changes.
 * A priority the user picked by hand always wins. Otherwise the request type's
 * defaultPriority (absent / null on the API row = no default) or the fallback.
 *
 * @param {{current: string, touched: boolean, defaultPriority?: string|null, fallback?: string, allowed?: string[]}} args
 */
export function nextPriority({ current, touched, defaultPriority, fallback = "normal", allowed }) {
  if (touched) return current;
  if (defaultPriority && (!allowed || allowed.includes(defaultPriority))) return defaultPriority;
  return fallback;
}

const isBlank = (v) =>
  v === undefined || v === null || (typeof v === "string" && v.trim() === "") ||
  (Array.isArray(v) && v.length === 0);

/**
 * Whether closing the modal would throw away something the user entered.
 * Compares the current form against the snapshot it opened with. Values the
 * modal set itself (the open-time defaults) are in `initial`, so a freshly
 * opened form is clean even with a preset client or parent priority.
 *
 * @param {Object} current - { title, description, requestType, requestTypeNote,
 *   priority, kind, assigneeId, companyId, intakeValues, entityLinkCount }
 * @param {Object} initial - same shape, captured on open
 * @returns {boolean}
 */
export function isCreateFormDirty(current = {}, initial = {}) {
  const textFields = ["title", "description", "requestType", "requestTypeNote", "assigneeId", "companyId"];
  for (const k of textFields) {
    const a = (current[k] || "").toString().trim();
    const b = (initial[k] || "").toString().trim();
    if (a !== b) return true;
  }
  if ((current.priority || "") !== (initial.priority || "")) return true;
  if ((current.kind || "") !== (initial.kind || "")) return true;
  if ((current.entityLinkCount || 0) !== (initial.entityLinkCount || 0)) return true;
  const intake = current.intakeValues || {};
  return Object.keys(intake).some((k) => !isBlank(intake[k]));
}

/**
 * What a close REQUEST (Close button, Cancel, Escape) should do. An outside
 * click is not a close request at all (SOUND-742) — the backdrop has no handler.
 * @param {{dirty: boolean, submitting?: boolean}} args
 * @returns {"close"|"confirm"|"ignore"}
 */
export function closeRequestAction({ dirty, submitting = false }) {
  if (submitting) return "ignore";
  return dirty ? "confirm" : "close";
}
